/**
 * Python language scanner using tree-sitter
 *
 * Extracts functions, classes, methods, imports, decorators, type hints,
 * docstrings, and module variables from Python source files.
 * Uses tree-sitter queries for declarative pattern matching.
 */

import * as path from 'node:path';
import type { Logger } from '@prosdevlab/kero';
import {
  type FileSystemValidator,
  NodeFileSystemValidator,
  validateFile,
} from '../utils/file-validator';
import { PYTHON_QUERIES } from './python-queries';
import type { TreeSitterNode } from './tree-sitter';
import { initTreeSitter, loadLanguage, type ParsedTree, parseCode } from './tree-sitter';
import type { CalleeInfo, Document, Scanner, ScannerCapabilities } from './types';

/** Generated file patterns to skip */
const GENERATED_PATTERNS = ['_pb2.py', '_pb2_grpc.py'];
const GENERATED_COMMENTS = ['# Generated by', '# DO NOT EDIT', '# Auto-generated'];

/**
 * Python scanner using tree-sitter for parsing
 */
export class PythonScanner implements Scanner {
  readonly language = 'python';
  readonly capabilities: ScannerCapabilities = {
    syntax: true,
    types: true,
    documentation: true,
  };

  private static readonly MAX_SNIPPET_LINES = 50;
  private fileValidator: FileSystemValidator;

  constructor(fileValidator: FileSystemValidator = new NodeFileSystemValidator()) {
    this.fileValidator = fileValidator;
  }

  canHandle(filePath: string): boolean {
    return path.extname(filePath).toLowerCase() === '.py';
  }

  private async validatePythonSupport(): Promise<void> {
    try {
      await initTreeSitter();
      await loadLanguage('python');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('tree-sitter WASM') || errorMessage.includes('Failed to locate')) {
        throw new Error(
          'Python tree-sitter WASM files not found. ' +
            'tree-sitter-python.wasm is required for Python code parsing.'
        );
      }
      throw error;
    }
  }

  async scan(
    files: string[],
    repoRoot: string,
    logger?: Logger,
    onProgress?: (filesProcessed: number, totalFiles: number) => void
  ): Promise<Document[]> {
    const documents: Document[] = [];
    const total = files.length;

    try {
      await this.validatePythonSupport();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger?.error({ error: errorMessage }, 'Python scanner initialization failed');
      throw error;
    }

    const startTime = Date.now();
    let lastLogTime = startTime;

    for (let i = 0; i < total; i++) {
      const file = files[i];

      if (onProgress && i > 0 && i % 50 === 0) {
        onProgress(i, total);
      }

      const now = Date.now();
      if (logger && i > 0 && (i % 50 === 0 || now - lastLogTime > 10000)) {
        lastLogTime = now;
        const percent = Math.round((i / total) * 100);
        logger.info(
          { filesProcessed: i, total, percent, documents: documents.length },
          `python ${i}/${total} (${percent}%) - ${documents.length} docs`
        );
      }

      try {
        const absolutePath = path.join(repoRoot, file);
        const validation = validateFile(file, absolutePath, this.fileValidator);
        if (!validation.isValid) continue;

        const sourceText = this.fileValidator.readText(absolutePath);

        if (this.isGeneratedFile(file, sourceText)) continue;

        const fileDocs = await this.extractFromFile(sourceText, file);
        documents.push(...fileDocs);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger?.debug({ file, error: errorMessage }, `Skipped Python file: ${file}`);
      }
    }

    logger?.info(
      { successCount: documents.length, total },
      `Python scan complete: ${documents.length} docs from ${total} files`
    );

    return documents;
  }

  private isGeneratedFile(filePath: string, sourceText: string): boolean {
    const basename = path.basename(filePath);
    if (GENERATED_PATTERNS.some((p) => basename.endsWith(p))) return true;
    if (filePath.includes('/migrations/') || filePath.includes('/versions/')) return true;

    const firstLines = sourceText.split('\n').slice(0, 3).join('\n');
    return GENERATED_COMMENTS.some((c) => firstLines.includes(c));
  }

  private async extractFromFile(sourceText: string, relativeFile: string): Promise<Document[]> {
    const documents: Document[] = [];
    const tree = await parseCode(sourceText, 'python');

    // Parse __all__ for export control
    const allExports = this.parseAllExports(sourceText);

    // Extract file-level imports
    const imports = this.extractImports(tree);

    // Extract functions (top-level + decorated)
    documents.push(...this.extractFunctions(tree, sourceText, relativeFile, allExports, imports));

    // Extract classes
    documents.push(...this.extractClasses(tree, sourceText, relativeFile, allExports));

    // Extract methods (inside classes + decorated)
    documents.push(...this.extractMethods(tree, sourceText, relativeFile));

    // Extract module-level variables
    documents.push(...this.extractModuleVariables(tree, sourceText, relativeFile, allExports));

    return documents;
  }

  // ========================================================================
  // Extraction methods
  // ========================================================================

  private extractFunctions(
    tree: ParsedTree,
    sourceText: string,
    file: string,
    allExports: Set<string> | null,
    imports: string[]
  ): Document[] {
    const documents: Document[] = [];

    // Regular top-level functions
    for (const match of tree.query(PYTHON_QUERIES.functions)) {
      const doc = this.functionMatchToDocument(match, sourceText, file, allExports, imports);
      if (doc) documents.push(doc);
    }

    // Decorated top-level functions
    for (const match of tree.query(PYTHON_QUERIES.decoratedFunctions)) {
      const doc = this.functionMatchToDocument(match, sourceText, file, allExports, imports);
      if (doc) documents.push(doc);
    }

    return documents;
  }

  private extractClasses(
    tree: ParsedTree,
    _sourceText: string,
    file: string,
    allExports: Set<string> | null
  ): Document[] {
    const documents: Document[] = [];

    for (const match of tree.query(PYTHON_QUERIES.classes)) {
      const nameCapture = match.captures.find((c) => c.name === 'name');
      const defCapture = match.captures.find((c) => c.name === 'definition');
      if (!nameCapture || !defCapture) continue;

      const name = nameCapture.node.text;
      const startLine = defCapture.node.startPosition.row + 1;
      const endLine = defCapture.node.endPosition.row + 1;
      const fullText = defCapture.node.text;
      const signature = this.extractSignature(fullText);
      const docstring = this.extractDocstring(defCapture.node);
      const exported = this.isExported(name, allExports);
      const snippet = this.truncateSnippet(fullText);

      documents.push({
        id: `${file}:${name}:${startLine}`,
        text: this.buildEmbeddingText('class', name, signature, docstring),
        type: 'class',
        language: 'python',
        metadata: {
          file,
          startLine,
          endLine,
          name,
          signature,
          exported,
          docstring,
          snippet,
        },
      });
    }

    return documents;
  }

  private extractMethods(tree: ParsedTree, _sourceText: string, file: string): Document[] {
    const documents: Document[] = [];

    // Regular methods
    for (const match of tree.query(PYTHON_QUERIES.methods)) {
      const doc = this.methodMatchToDocument(match, _sourceText, file);
      if (doc) documents.push(doc);
    }

    // Decorated methods
    for (const match of tree.query(PYTHON_QUERIES.decoratedMethods)) {
      const doc = this.methodMatchToDocument(match, _sourceText, file);
      if (doc) documents.push(doc);
    }

    return documents;
  }

  private extractModuleVariables(
    tree: ParsedTree,
    _sourceText: string,
    file: string,
    allExports: Set<string> | null
  ): Document[] {
    const documents: Document[] = [];

    for (const match of tree.query(PYTHON_QUERIES.moduleVariables)) {
      const nameCapture = match.captures.find((c) => c.name === 'name');
      const defCapture = match.captures.find((c) => c.name === 'definition');
      if (!nameCapture || !defCapture) continue;

      const name = nameCapture.node.text;
      // Skip __all__ itself and dunder names
      if (name === '__all__' || (name.startsWith('__') && name.endsWith('__'))) continue;

      const startLine = defCapture.node.startPosition.row + 1;
      const endLine = defCapture.node.endPosition.row + 1;
      const fullText = defCapture.node.text;
      const exported = this.isExported(name, allExports);
      const snippet = this.truncateSnippet(fullText);

      documents.push({
        id: `${file}:${name}:${startLine}`,
        text: `variable ${name}: ${fullText.trim()}`,
        type: 'variable',
        language: 'python',
        metadata: {
          file,
          startLine,
          endLine,
          name,
          signature: fullText.split('\n')[0].trim(),
          exported,
          snippet,
        },
      });
    }

    return documents;
  }

  // ========================================================================
  // Helper methods
  // ========================================================================

  private functionMatchToDocument(
    match: { captures: Array<{ name: string; node: TreeSitterNode }> },
    _sourceText: string,
    file: string,
    allExports: Set<string> | null,
    imports: string[]
  ): Document | null {
    const nameCapture = match.captures.find((c) => c.name === 'name');
    const defCapture = match.captures.find((c) => c.name === 'definition');
    if (!nameCapture || !defCapture) return null;

    const name = nameCapture.node.text;
    const startLine = defCapture.node.startPosition.row + 1;
    const endLine = defCapture.node.endPosition.row + 1;
    const fullText = defCapture.node.text;
    const signature = this.extractSignature(fullText);
    const docstring = this.extractDocstring(defCapture.node);
    const exported = this.isExported(name, allExports);
    const snippet = this.truncateSnippet(fullText);
    // Check for 'async def' anywhere in the text (handles decorators above)
    const isAsync = /\basync\s+def\b/.test(fullText);
    const callees = this.extractCallees(defCapture.node);

    return {
      id: `${file}:${name}:${startLine}`,
      text: this.buildEmbeddingText('function', name, signature, docstring),
      type: 'function',
      language: 'python',
      metadata: {
        file,
        startLine,
        endLine,
        name,
        signature,
        exported,
        docstring,
        snippet,
        imports: imports.length > 0 ? imports : undefined,
        callees: callees.length > 0 ? callees : undefined,
        isAsync: isAsync || undefined,
      },
    };
  }

  private methodMatchToDocument(
    match: { captures: Array<{ name: string; node: TreeSitterNode }> },
    _sourceText: string,
    file: string
  ): Document | null {
    const nameCapture = match.captures.find((c) => c.name === 'name');
    const defCapture = match.captures.find((c) => c.name === 'definition');
    if (!nameCapture || !defCapture) return null;

    const name = nameCapture.node.text;
    const startLine = defCapture.node.startPosition.row + 1;
    const endLine = defCapture.node.endPosition.row + 1;
    const fullText = defCapture.node.text;
    const signature = this.extractSignature(fullText);
    const docstring = this.extractDocstring(defCapture.node);
    const snippet = this.truncateSnippet(fullText);
    const isAsync = /\basync\s+def\b/.test(fullText);
    const callees = this.extractCallees(defCapture.node);

    // Methods are not individually exported — the class controls visibility
    return {
      id: `${file}:${name}:${startLine}`,
      text: this.buildEmbeddingText('method', name, signature, docstring),
      type: 'method',
      language: 'python',
      metadata: {
        file,
        startLine,
        endLine,
        name,
        signature,
        exported: !name.startsWith('_'),
        docstring,
        snippet,
        callees: callees.length > 0 ? callees : undefined,
        isAsync: isAsync || undefined,
      },
    };
  }

  private extractImports(tree: ParsedTree): string[] {
    const imports: string[] = [];

    for (const match of tree.query(PYTHON_QUERIES.imports)) {
      const def = match.captures.find((c) => c.name === 'definition');
      if (def) imports.push(def.node.text.trim());
    }

    for (const match of tree.query(PYTHON_QUERIES.fromImports)) {
      const def = match.captures.find((c) => c.name === 'definition');
      if (def) imports.push(def.node.text.trim());
    }

    return imports;
  }

  /**
   * Extract docstring from a function/class node.
   * Python docstrings are the first expression_statement > string in the body block.
   */
  private extractDocstring(node: TreeSitterNode): string | undefined {
    // For decorated_definition, look inside the inner function/class
    let target = node;
    if (node.type === 'decorated_definition') {
      const inner = node.children?.find(
        (c: TreeSitterNode) => c.type === 'function_definition' || c.type === 'class_definition'
      );
      if (inner) target = inner;
    }

    // Navigate to body > block > first child
    const body = target.children?.find((c: TreeSitterNode) => c.type === 'block');
    if (!body) return undefined;

    for (const child of body.children || []) {
      if (child.type === 'expression_statement') {
        const str = child.children?.find((c: TreeSitterNode) => c.type === 'string');
        if (str) {
          // Remove triple quotes and whitespace
          let text = str.text;
          if (text.startsWith('"""') && text.endsWith('"""')) {
            text = text.slice(3, -3).trim();
          } else if (text.startsWith("'''") && text.endsWith("'''")) {
            text = text.slice(3, -3).trim();
          } else if (text.startsWith('"') && text.endsWith('"')) {
            text = text.slice(1, -1).trim();
          } else if (text.startsWith("'") && text.endsWith("'")) {
            text = text.slice(1, -1).trim();
          }
          return text || undefined;
        }
        break; // First non-docstring statement means no docstring
      }
      // Skip newlines, comments
      if (child.type !== 'comment' && child.type !== 'newline' && !child.type.includes('MISSING')) {
        break;
      }
    }

    return undefined;
  }

  /**
   * Extract callees from function/method body.
   * Walks ALL call nodes at any depth (matches TypeScript scanner behavior).
   */
  private extractCallees(node: TreeSitterNode): CalleeInfo[] {
    const callees: CalleeInfo[] = [];
    const seen = new Set<string>();

    this.walkCallNodes(node, (callNode: TreeSitterNode) => {
      const funcNode = callNode.children?.find(
        (c: TreeSitterNode) => c.type === 'identifier' || c.type === 'attribute'
      );
      if (!funcNode) return;

      const name = funcNode.text; // e.g., "db.get" for attribute, "foo" for identifier
      const line = callNode.startPosition.row + 1;
      const key = `${name}:${line}`;

      if (!seen.has(key)) {
        seen.add(key);
        callees.push({ name, line });
      }
    });

    return callees;
  }

  private walkCallNodes(node: TreeSitterNode, callback: (node: TreeSitterNode) => void): void {
    if (!node) return;
    if (node.type === 'call') {
      callback(node);
    }
    for (const child of node.children || []) {
      this.walkCallNodes(child, callback);
    }
  }

  /**
   * Parse __all__ = [...] from source text.
   * Returns set of exported names, or null if __all__ is not defined.
   */
  private parseAllExports(sourceText: string): Set<string> | null {
    // Simple regex for __all__ = ['name1', 'name2'] or ["name1", "name2"]
    const match = sourceText.match(/__all__\s*=\s*\[([^\]]*)\]/);
    if (!match) return null;

    const names = new Set<string>();
    const content = match[1];
    const namePattern = /['"](\w+)['"]/g;
    for (const m of content.matchAll(namePattern)) {
      names.add(m[1]);
    }

    return names.size > 0 ? names : null;
  }

  private isExported(name: string, allExports: Set<string> | null): boolean {
    if (allExports) {
      return allExports.has(name);
    }
    // Convention: underscore prefix = private
    return !name.startsWith('_');
  }

  private extractSignature(fullText: string): string {
    // Find the 'def' or 'async def' line (skips decorator lines)
    const lines = fullText.split('\n');
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('def ') || trimmed.startsWith('async def ')) {
        return trimmed.endsWith(':') ? trimmed.slice(0, -1).trim() : trimmed;
      }
      if (trimmed.startsWith('class ')) {
        return trimmed.endsWith(':') ? trimmed.slice(0, -1).trim() : trimmed;
      }
    }
    // Fallback: first line
    const firstLine = lines[0].trim();
    return firstLine.endsWith(':') ? firstLine.slice(0, -1).trim() : firstLine;
  }

  private truncateSnippet(text: string): string {
    const lines = text.split('\n');
    if (lines.length <= PythonScanner.MAX_SNIPPET_LINES) return text;
    return `${lines.slice(0, PythonScanner.MAX_SNIPPET_LINES).join('\n')}\n...`;
  }

  private buildEmbeddingText(
    type: string,
    name: string,
    signature: string,
    docstring?: string
  ): string {
    const parts = [`${type} ${name}`, signature];
    if (docstring) parts.push(docstring);
    return parts.join('\n');
  }
}
